/**
 * A stable 32-bit string hash — the id every theme preset is known by.
 *
 * **In a file of its own, with no imports.** It used to live in `formatter.ts`, which opens with
 * `import { type JssStyle } from 'jss'`; that made the theme presets — pure data and pure functions
 * — unreachable from a node service without dragging JSS in with them. `apps/mcp-backend` needs the
 * presets to apply one on an AI call, and it must compute the SAME id the editor's panel does, or a
 * preset applied by the AI would show as "custom" in the panel that offered it.
 *
 * `formatter.ts` re-exports it, so every existing caller is unaffected.
 *
 * Deliberately bug-compatible: the original declared `str` without a value, so the first `+=`
 * stringified `undefined` and EVERY hash is really of `'undefined' + …`. That is written out here
 * instead of fixed, because fixing it would renumber every shipped preset — and `selectedIndex`
 * values already persisted in themes across the estate would stop matching anything, so every site
 * would show its palette as "custom". Spelling it out also makes the function compile under
 * `strictNullChecks`, which the original did not: it only ever built in projects that had it off.
 */
export function hashCode(...values: any[]): string {
    let hash = 0;
    let i;
    let chr;
    let str = String(undefined);

    values.forEach((value) => {
        str += typeof value === 'string' ? value : JSON.stringify(value);
    });

    const len = str.length;

    if (len === 0) {
        return hash.toString();
    }

    for (i = 0; i < len; i++) {
        chr = str.charCodeAt(i);
        hash = (hash << 5) - hash + chr;
        hash |= 0; // convert to 32bit integer
    }

    return hash.toString();
}
